import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'unistore/react'
import i18n from 'i18next'
import injectSheet from 'react-jss'
import * as actions from '../helpers/actions.jsx'
import {Paperclip, Trash} from '../Icons/index.jsx'
import Progress from './Progress.jsx'

export const filePropTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  uploading: PropTypes.bool.isRequired,
  count: PropTypes.number,
  text: PropTypes.string,
  url: PropTypes.string,
  uploadFailed: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  countFailed: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
}

class Upload extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    fileIsEditable: PropTypes.bool,
    contactForm: PropTypes.bool.isRequired,
    showWordCount: PropTypes.bool,
    //
    ...filePropTypes,
    // store actions
    removeFile: PropTypes.func.isRequired,
    setFileCount: PropTypes.func.isRequired,
  }

  static defaultProps = {
    contactForm: false,
    fileIsEditable: true,
    showWordCount: true,
  }

  removeFile = (name) => () => this.props.removeFile(name)

  setFileCount = (event) =>
    this.props.setFileCount(this.props.name, event.target.value)

  // formula: https://app.asana.com/0/812691287214473/958239343412407/f
  computeProgressTime = (size = 10000, type = '.txt') => {
    const MIN_SIZE = 10000

    const fileSize = size >= MIN_SIZE ? size : MIN_SIZE
    const time_per_10KB = 5
    const avg_complete_perc = 0.5
    const pdf_factor = 10

    const isPdf = !!type.match(/pdf/i)
    const time =
      ((Math.log10(fileSize / 1000) * time_per_10KB) / avg_complete_perc) *
      (isPdf ? pdf_factor : 1)

    const result = Math.round(time)

    return result
  }

  debugFile = (event) => {
    if (!event.shiftKey) return

    const {name, count, text} = this.props
    const tab = window.open(name, '_blank')
    tab.document.write('<pre>Count:' + count + '</pre>')
    tab.document.write('<pre>' + text + '</pre>')
    tab.focus()
  }

  render = () => {
    const {
      classes,
      fileIsEditable,
      name,
      size,
      type,
      uploading,
      count,
      uploadFailed,
      countFailed,
      contactForm,
      showWordCount,
    } = this.props
    const {computeProgressTime, setFileCount, removeFile, debugFile} = this

    const progressTime = computeProgressTime(size, type)

    /* data-upload attribute needed: we are going to filter dropzone events on this element to prevent dialog from opening when clicking on uploads */
    return (
      <div className={classes.root} data-upload onClick={debugFile}>
        <div className={classes.main}>
          <Paperclip className={classes.icon} />
          <p className={classes.name}>{name}</p>
          {/* <small className={classes.size}>{`(${filesize(size, { base: 10, round: 0, spacer: '' })})`}</small> */}
          {!uploading && !contactForm && showWordCount && (
            <small
              className={classes.size}
            >{`(${count.toLocaleString()} ${i18n.t(
              'Components.WordCountOrUpload.words',
            )})`}</small>
          )}
          {fileIsEditable && (
            <Trash className={classes.icon} onClick={removeFile(name)} />
          )}
          {uploading && (
            <Progress className={classes.progress} seconds={progressTime} />
          )}
        </div>
        {fileIsEditable && (!!countFailed || !!uploadFailed) && (
          <div className={classes.manualWordCount}>
            {/* when page is reloaded while file is uploading, in store.js we set uploadFailed=true */}
            {!!uploadFailed &&
              typeof uploadFailed === 'string' &&
              uploadFailed.startsWith('ERR_UPLOAD_1') && (
                <span>{i18n.t('Components.Uploader.fileIssue')}</span>
              )}
            {!!uploadFailed &&
              typeof uploadFailed === 'string' &&
              !uploadFailed.startsWith('ERR_UPLOAD_1') && (
                <span>{i18n.t('Components.Uploader.uploadIssue')}</span>
              )}
            {!!uploadFailed && typeof uploadFailed !== 'string' && (
              <span>{i18n.t('Components.Uploader.uploadIssue')}</span>
            )}
            {!!countFailed && (
              <>
                <span>{i18n.t('Components.Uploader.countIssue')}</span>
                <label>
                  {i18n.t('Components.Uploader.promptEstimatedWordCount')}
                </label>
                <input value={count} onChange={setFileCount} />
                <span
                  dangerouslySetInnerHTML={{
                    __html: i18n.t('Components.Uploader.contactUsForWordCount'),
                  }}
                />
              </>
            )}
          </div>
        )}
      </div>
    )
  }
}

const Component = connect([], actions)(Upload)

export default injectSheet({
  root: {
    position: 'relative',
    width: '100%',
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 2,
    boxShadow: '0 0 0 1px #ccd4de',
    cursor: 'default',
    marginBottom: 24,
  },
  main: {
    display: 'flex',
    alignItems: 'center',
  },
  name: {
    flexGrow: 1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '230px',
    padding: [0, 12],
  },
  size: {
    paddingRight: 12,
    color: '#666',
    fontSize: 14,
    whiteSpace: 'nowrap',
  },
  icon: {
    backgroundColor: '#fff',
    fill: '#666',
    boxShadow: '0 0 0 1px #ccd4de',
    fontWeight: 400,
    padding: 8,
    width: 38,
    height: 38,
    cursor: 'pointer',
  },
  progress: {
    position: 'absolute',
    left: 65,
    top: 48,
    width: 'calc(100% - 130px)',
  },
  manualWordCount: {
    marginTop: 12,
    width: '100%',
    fontSize: 14,

    '& > span:nth-of-type(1)': {
      display: 'block',
      color: '#ed7a5b',
      marginBottom: 5,
    },

    '& > label': {
      fontSize: 'inherit', //global styles are applied label...
    },

    '& > input': {
      marginLeft: 5,
      height: 28,
      width: 75,
      padding: [0, 10],
      textAlign: 'right',
    },

    '& > span:last-of-type': {
      marginTop: 5,
      display: 'block',
    },
  },
})(Component)
